import { FC, useEffect, useState } from 'react';

import { Box } from '@bonusly/design-system';

import { useIsAdminRoute } from '../use-is-admin-route';

interface Petal {
	id: number;
	left: number;
	animationDuration: number;
	animationDelay: number;
	size: number;
	emoji: string;
	rotation: number;
	swayAmount: number;
}

export const FallingPetals: FC = () => {
	const isAdminRoute = useIsAdminRoute();
	const [petals, setPetals] = useState<Petal[]>([]);

	useEffect(() => {
		const generated: Petal[] = [];

		for (let i = 0; i < 8; i++) {
			const rand = Math.random();
			let emoji: string;
			if (rand < 0.45) {
				emoji = '🌸'; // cherry blossom
			} else if (rand < 0.7) {
				emoji = '🏵️'; // rosette
			} else if (rand < 0.85) {
				emoji = '🦋'; // butterfly
			} else if (rand < 0.93) {
				emoji = '🌷'; // tulip
			} else {
				emoji = '🐝'; // bee
			}

			generated.push({
				id: i,
				left: Math.random() * 100,
				animationDuration: 12 + Math.random() * 14, // 12-26 seconds
				animationDelay: Math.random() * 18,
				size: 16 + Math.random() * 14, // 16-30px
				emoji,
				rotation: Math.random() * 360,
				swayAmount: 30 + Math.random() * 70, // 30-100px
			});
		}

		setPetals(generated);
	}, []);

	// Keep admin pages business-focused — no falling petals on /admin/*.
	if (isAdminRoute) return null;

	return (
		<Box
			position="fixed"
			top={0}
			left={0}
			width="100vw"
			height="100vh"
			pointerEvents="none"
			zIndex={9999}
			overflow="hidden"
			opacity={0}
			animation="springFadeIn 1.5s ease-in forwards"
			sx={{
				'@keyframes springFadeIn': {
					from: { opacity: 0 },
					to: { opacity: 1 },
				},
			}}
		>
			{petals.map((petal) => (
				<Box
					key={petal.id}
					position="absolute"
					top={-50}
					left={`${petal.left}%`}
					fontSize={`${petal.size}px`}
					opacity={0.65}
					style={{
						animation: `spring-fall-${petal.id} ${petal.animationDuration}s linear ${petal.animationDelay}s infinite`,
						transform: `rotate(${petal.rotation}deg)`,
					}}
				>
					<style>
						{`
							@keyframes spring-fall-${petal.id} {
								0% {
									transform: translateY(-50px) translateX(0px) rotate(${petal.rotation}deg);
									opacity: 0;
								}
								5% {
									opacity: 0.65;
								}
								25% {
									transform: translateY(25vh) translateX(${petal.swayAmount}px) rotate(${petal.rotation + 90}deg);
								}
								50% {
									transform: translateY(50vh) translateX(-${petal.swayAmount * 0.3}px) rotate(${petal.rotation + 180}deg);
								}
								75% {
									transform: translateY(75vh) translateX(${petal.swayAmount * 0.6}px) rotate(${petal.rotation + 270}deg);
								}
								95% {
									opacity: 0.65;
								}
								100% {
									transform: translateY(100vh) translateX(0px) rotate(${petal.rotation + 360}deg);
									opacity: 0;
								}
							}
						`}
					</style>
					{petal.emoji}
				</Box>
			))}
		</Box>
	);
};
